import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  IconButton,
  Typography,
  Chip,
  CircularProgress,
  Tooltip,
  Autocomplete,
  Snackbar,
  Alert,
} from "@mui/material";
import { createPortal } from "react-dom";
import MaterialReactTable from "material-react-table";
import { ExportToCsv } from "export-to-csv";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import RefreshIcon from "@mui/icons-material/Refresh";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import lambdaFetch from "../functions/FetchFromLambda";
import FilterLogicBuilder from "./FilterLogicBuilder";

const { REACT_APP_STAGE, REACT_APP_ENABLE_COMPANY_SECURITY_UI } = process.env;
const enableCompanySecurityUi = String(REACT_APP_ENABLE_COMPANY_SECURITY_UI).toLowerCase() === "true";

const csvOptions = {
  fieldSeparator: ",",
  quoteStrings: '"',
  decimalSeparator: ".",
  showLabels: true,
  useBom: true,
  useKeysAsHeaders: true,
};

const csvExporter = new ExportToCsv(csvOptions);

const STATEMENTS_CONFIG = {
  name: "Data Role Statements",
  endpoint: "statements",
  tableName: `apix_${REACT_APP_STAGE}_sys_data_role_statements`,
  primaryKey: "id",
  description: "Data access control statements",
  editable: true,
  autoGenerateId: true,
  idPrefix: "STMT_",
  customColumns: [
    { accessorKey: "data_role", header: "Data Role", size: 150 },
    { accessorKey: "query", header: "Query", size: 200 },
    { accessorKey: "filter_logic", header: "Filter Logic", size: 400 },
    ...(enableCompanySecurityUi
      ? [{ accessorKey: "security_scopes", header: "Security Scopes", size: 300 }]
      : []),
    { accessorKey: "active", header: "Active", size: 100 },
  ],
};

const JSON_FIELDS = new Set(["filter_logic", "security_scopes"]);

export default function StatementsTable({ credentials }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState("create");
  const [currentItem, setCurrentItem] = useState({});
  const [editingItem, setEditingItem] = useState(null);
  const [availableDataRoles, setAvailableDataRoles] = useState([]);
  const [availableQueries, setAvailableQueries] = useState([]);
  const [loadingQueries, setLoadingQueries] = useState(false);
  const [selectedQueryDetails, setSelectedQueryDetails] = useState(null);
  const [loadingQueryDetails, setLoadingQueryDetails] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [itemPendingDelete, setItemPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Pagination state
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 100,
    totalCount: 0,
    lastEvaluatedKey: null,
    hasNextPage: false,
  });

  const sortKeysDeep = (value) => {
    if (Array.isArray(value)) {
      return value.map(sortKeysDeep);
    }
    if (value && typeof value === "object") {
      const sortedEntries = Object.entries(value).sort(([a], [b]) =>
        a.localeCompare(b),
      );
      return sortedEntries.reduce((acc, [key, val]) => {
        acc[key] = sortKeysDeep(val);
        return acc;
      }, {});
    }
    return value;
  };

  const parseJsonField = (value, fieldLabel) => {
    if (value === "" || value === null || value === undefined) {
      return {};
    }

    if (typeof value === "object") {
      return value;
    }

    try {
      return JSON.parse(value);
    } catch {
      throw new Error(`${fieldLabel} must be valid JSON`);
    }
  };

  // Fetch data with pagination
  const fetchTableData = async (
    pageIndex = 0,
    pageSize = 100,
    lastEvaluatedKey = null,
  ) => {
    setLoading(true);
    setError(null);

    try {
      const endpoint = `dynamo/${STATEMENTS_CONFIG.endpoint}/scan`;
      const requestBody = {
        limit: pageSize,
        lastEvaluatedKey,
      };

      const response = await lambdaFetch(
        endpoint,
        "post",
        credentials.user.accessToken,
        JSON.stringify(requestBody),
        "",
        credentials,
      );

      let parsedData = [];
      let paginationInfo = {
        lastEvaluatedKey: null,
        count: 0,
        scannedCount: 0,
      };

      if (response && response.data) {
        if (Array.isArray(response.data)) {
          parsedData = response.data;
        } else if (response.data.body) {
          let bodyObj = response.data.body;
          if (typeof response.data.body === "string") {
            try {
              bodyObj = JSON.parse(response.data.body);
            } catch (e) {
              console.error("❌ Failed to parse body:", e);
              setError("Failed to parse response body");
              setLoading(false);
              return;
            }
          }

          let finalData = bodyObj?.data;
          if (typeof bodyObj?.data === "string") {
            try {
              finalData = JSON.parse(bodyObj.data);
            } catch (e) {
              console.error("❌ Failed to parse body.data:", e);
            }
          }

          parsedData = Array.isArray(finalData)
            ? finalData
            : finalData
              ? [finalData]
              : [];

          paginationInfo = {
            lastEvaluatedKey: bodyObj?.lastEvaluatedKey || null,
            count: bodyObj?.count || parsedData.length,
            scannedCount: bodyObj?.scannedCount || parsedData.length,
          };
        } else if (response.data.data) {
          parsedData = Array.isArray(response.data.data)
            ? response.data.data
            : [response.data.data];
          paginationInfo = {
            lastEvaluatedKey: response.data.lastEvaluatedKey || null,
            count: response.data.count || parsedData.length,
            scannedCount: response.data.scannedCount || parsedData.length,
          };
        }

        // Normalize JSON fields
        const normalizedData = parsedData.map((item) => {
          if (!item) {
            return item;
          }

          const normalizedItem = { ...item };
          JSON_FIELDS.forEach((field) => {
            if (typeof normalizedItem[field] === "string") {
              try {
                normalizedItem[field] = JSON.parse(normalizedItem[field]);
              } catch (e) {
                // Leave invalid legacy strings untouched so they can still be viewed/fixed.
              }
            }
          });
          return normalizedItem;
        });

        setData(normalizedData);
        setPagination((prev) => ({
          ...prev,
          pageIndex,
          pageSize,
          lastEvaluatedKey: paginationInfo.lastEvaluatedKey,
          hasNextPage: !!paginationInfo.lastEvaluatedKey,
          totalCount: prev.totalCount + parsedData.length,
        }));
        setError(null);
      } else {
        setError("Failed to fetch data");
      }
    } catch (err) {
      console.error("Error fetching statements:", err);
      setError(err.message || "Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  // Load data on mount
  useEffect(() => {
    fetchTableData(0, 100, null);
    fetchAvailableDataRoles();
    fetchAvailableQueries(); // Fetch queries once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAvailableDataRoles = async () => {
    try {
      // Fetch roles from the roles table with filter for active=true and type="data"
      const endpoint = "dynamo/roles/scan";
      const requestBody = {
        limit: 1000,
        filterExpression: "#active = :active AND #type = :type",
        expressionAttributeNames: {
          "#active": "active",
          "#type": "type",
        },
        expressionAttributeValues: {
          ":active": true,
          ":type": "data",
        },
      };

      const response = await lambdaFetch(
        endpoint,
        "post",
        credentials.user.accessToken,
        JSON.stringify(requestBody),
        "",
        credentials,
      );

      let roles = [];
      if (response && response.data) {
        if (Array.isArray(response.data)) {
          roles = response.data;
        } else if (response.data.body) {
          const bodyObj =
            typeof response.data.body === "string"
              ? JSON.parse(response.data.body)
              : response.data.body;
          const finalData = typeof bodyObj?.data === "string"
            ? JSON.parse(bodyObj.data)
            : bodyObj?.data;
          roles = Array.isArray(finalData) ? finalData : finalData ? [finalData] : [];
        } else if (response.data.data) {
          roles = Array.isArray(response.data.data) ? response.data.data : [response.data.data];
        }

        // Extract role_name from each role and filter out any null/undefined values
        const dataRoles = roles
          .map((role) => role.role_name)
          .filter((name) => name != null && name !== "");

        setAvailableDataRoles(dataRoles);
      } else {
        setAvailableDataRoles([]);
      }
    } catch (error) {
      console.error("❌ Error fetching available data roles:", error);
      setAvailableDataRoles([]);
    }
  };

  const fetchAvailableQueries = async () => {
    setLoadingQueries(true);
    try {
      const response = await lambdaFetch(
        "get-queries",
        "post",
        credentials.user.accessToken,
        JSON.stringify({ action: "list" }),
        "",
        credentials,
      );

      let queries = [];
      if (response && response.data) {
        if (response.data.queries) {
          queries = response.data.queries;
        } else if (response.data.body) {
          const bodyObj =
            typeof response.data.body === "string"
              ? JSON.parse(response.data.body)
              : response.data.body;
          queries = bodyObj.queries || [];
        }
      }

      // Add "default" as a special query option (fallback logic with no SQL)
      const defaultQuery = {
        name: "default",
        key: "default",
        isDefault: true,
      };
      
      setAvailableQueries([defaultQuery, ...queries]);
    } catch (error) {
      console.error("❌ Error fetching available queries:", error);
      // Still add default even if fetch fails
      setAvailableQueries([{ name: "default", key: "default", isDefault: true }]);
    } finally {
      setLoadingQueries(false);
    }
  };

  const fetchQueryDetails = async (queryName) => {
    if (!queryName) {
      setSelectedQueryDetails(null);
      return;
    }

    // Handle "default" query - no SQL, just a fallback logic
    if (queryName === "default") {
      setSelectedQueryDetails({
        name: "default",
        description: "Default fallback logic. Used when no specific query logic is defined for a role. This query has no SQL associated with it.",
        sql: null,
        fields: [],
        isDefault: true,
      });
      return;
    }

    setLoadingQueryDetails(true);
    try {
      const response = await lambdaFetch(
        "get-queries",
        "post",
        credentials.user.accessToken,
        JSON.stringify({ action: "get", queryName }),
        "",
        credentials,
      );

      let queryDetails = null;
      if (response && response.data) {
        if (response.data.query) {
          queryDetails = response.data.query;
        } else if (response.data.body) {
          const bodyObj =
            typeof response.data.body === "string"
              ? JSON.parse(response.data.body)
              : response.data.body;
          queryDetails = bodyObj.query || null;
        }
      }

      setSelectedQueryDetails(queryDetails);
    } catch (error) {
      console.error("❌ Error fetching query details:", error);
      setSelectedQueryDetails(null);
      showSnackbar("Failed to load query details", "error");
    } finally {
      setLoadingQueryDetails(false);
    }
  };

  const handleRefresh = () => {
    setPagination({
      pageIndex: 0,
      pageSize: 100,
      totalCount: 0,
      lastEvaluatedKey: null,
      hasNextPage: false,
    });
    fetchTableData(0, 100, null);
    fetchAvailableDataRoles(); // Refresh available data roles
  };

  const handlePaginationChange = (newPageIndex, newPageSize) => {
    if (newPageSize !== pagination.pageSize) {
      setPagination((prev) => ({
        ...prev,
        pageIndex: 0,
        pageSize: newPageSize,
        totalCount: 0,
        lastEvaluatedKey: null,
        hasNextPage: false,
      }));
      fetchTableData(0, newPageSize, null);
    } else if (newPageIndex !== pagination.pageIndex) {
      let lastEvaluatedKeyToUse = null;
      if (newPageIndex > pagination.pageIndex) {
        lastEvaluatedKeyToUse = pagination.lastEvaluatedKey;
      } else if (newPageIndex < pagination.pageIndex) {
        lastEvaluatedKeyToUse = null;
      }

      setPagination((prev) => ({
        ...prev,
        pageIndex: newPageIndex,
      }));

      fetchTableData(newPageIndex, newPageSize, lastEvaluatedKeyToUse);
    }
  };

  const handleAddClick = async () => {
    setDialogMode("create");
    setEditingItem(null);
    setCurrentItem({ active: true });
    setSelectedQueryDetails(null);
    // Data roles and queries are already loaded on mount, no need to fetch again
    setDialogOpen(true);
  };

  const handleEditClick = async (item) => {
    setDialogMode("edit");
    setCurrentItem({ ...item });
    setEditingItem(item);
    setSelectedQueryDetails(null);
    // Data roles and queries are already loaded on mount, no need to fetch again
    // Open modal immediately
    setDialogOpen(true);
    // If item has a query, fetch its details in the background
    if (item.query) {
      await fetchQueryDetails(item.query);
    }
  };

  const handleDeleteClick = (item) => {
    setItemPendingDelete(item);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteCancel = (event, reason) => {
    // Only close on button click, not on backdrop click or escape key
    if (reason && (reason === "backdropClick" || reason === "escapeKeyDown")) {
      return;
    }
    // Don't allow canceling while deleting
    if (deleting) {
      return;
    }
    setDeleteConfirmOpen(false);
    setItemPendingDelete(null);
  };

  const handleDeleteConfirm = async () => {
    if (!itemPendingDelete) {
      return;
    }

    setDeleting(true);

    try {
      const primaryKeyValue = itemPendingDelete[STATEMENTS_CONFIG.primaryKey];
      const path = `dynamo/${STATEMENTS_CONFIG.endpoint}/item/${primaryKeyValue}`;

      const response = await lambdaFetch(
        path,
        "delete",
        credentials.user.accessToken,
        null,
        "Item deleted successfully",
        credentials,
      );

      if (response && response.success) {
        fetchTableData(pagination.pageIndex, pagination.pageSize, null);
      }
    } catch (error) {
      console.error("Error deleting item:", error);
      showSnackbar("Error deleting item. Please try again.", "error");
    } finally {
      setDeleting(false);
      setDeleteConfirmOpen(false);
      setItemPendingDelete(null);
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setCurrentItem({});
    setEditingItem(null);
    setSelectedQueryDetails(null);
  };

  const handleSave = async () => {
    try {
      // Validate that data_role and query are provided
      if (!currentItem.data_role || !currentItem.query) {
        showSnackbar("Data Role and Query are required fields", "error");
        return;
      }

      // Check for duplicate data_role + query combination
      const duplicateStatement = data.find((item) => {
        // Skip the current item if editing
        if (dialogMode === "edit" && editingItem) {
          if (item[STATEMENTS_CONFIG.primaryKey] === editingItem[STATEMENTS_CONFIG.primaryKey]) {
            return false;
          }
        }
        return (
          item.data_role === currentItem.data_role &&
          item.query === currentItem.query
        );
      });

      if (duplicateStatement) {
        showSnackbar(
          `A statement with Data Role "${currentItem.data_role}" and Query "${currentItem.query}" already exists. Please edit the existing statement instead.`,
          "error"
        );
        return;
      }

      let response;

      if (dialogMode === "create") {
        let itemToCreate = { ...currentItem };

        if (
          STATEMENTS_CONFIG.autoGenerateId &&
          !itemToCreate[STATEMENTS_CONFIG.primaryKey]
        ) {
          const timestamp = Date.now();
          const random = Math.random().toString(36).substr(2, 9);
          itemToCreate[STATEMENTS_CONFIG.primaryKey] =
            `${STATEMENTS_CONFIG.idPrefix}${timestamp}_${random}`;
        }

        try {
          itemToCreate.filter_logic = parseJsonField(itemToCreate.filter_logic, "Filter Logic");
          if (enableCompanySecurityUi) {
            itemToCreate.security_scopes = parseJsonField(itemToCreate.security_scopes, "Security Scopes");
          } else {
            delete itemToCreate.security_scopes;
          }
        } catch (e) {
          showSnackbar(e.message, "error");
          return;
        }

        // If filter_logic is empty or null, set it to wide open structure (1=1)
        if (!itemToCreate.filter_logic || 
            (typeof itemToCreate.filter_logic === "object" && 
             (!itemToCreate.filter_logic.conditions || itemToCreate.filter_logic.conditions.length === 0))) {
          itemToCreate.filter_logic = {
            operator: "AND",
            conditions: []
          };
        }

        response = await lambdaFetch(
          `dynamo/${STATEMENTS_CONFIG.endpoint}`,
          "post",
          credentials.user.accessToken,
          JSON.stringify(itemToCreate),
          "Item created successfully",
          credentials,
        );
      } else {
        const primaryKeyValue = editingItem[STATEMENTS_CONFIG.primaryKey];
        const path = `dynamo/${STATEMENTS_CONFIG.endpoint}/item/${primaryKeyValue}`;

        const updates = { ...currentItem };
        delete updates[STATEMENTS_CONFIG.primaryKey];
        delete updates.created;
        delete updates.updated;
        delete updates.id;

        try {
          updates.filter_logic = parseJsonField(updates.filter_logic, "Filter Logic");
          if (enableCompanySecurityUi) {
            updates.security_scopes = parseJsonField(updates.security_scopes, "Security Scopes");
          } else {
            delete updates.security_scopes;
          }
        } catch (e) {
          showSnackbar(e.message, "error");
          return;
        }

        // If filter_logic is empty or null, set it to wide open structure (1=1)
        if (!updates.filter_logic || 
            (typeof updates.filter_logic === "object" && 
             (!updates.filter_logic.conditions || updates.filter_logic.conditions.length === 0))) {
          updates.filter_logic = {
            operator: "AND",
            conditions: []
          };
        }

        response = await lambdaFetch(
          path,
          "put",
          credentials.user.accessToken,
          JSON.stringify(updates),
          "Item updated successfully",
          credentials,
        );
      }

      if (response && response.success) {
        handleDialogClose();
        fetchTableData(pagination.pageIndex, pagination.pageSize, null);
      } else {
        console.error("❌ Save failed:", response);
      }
    } catch (error) {
      console.error("❌ Error saving item:", error);
    }
  };

  const handleInputChange = (field, value) => {
    setCurrentItem((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const showSnackbar = (message, severity = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  // Generate columns
  const columns = useMemo(() => {
    const cols = STATEMENTS_CONFIG.customColumns.map((col) => ({
      ...col,
      // Custom filter function for JSON columns to search within JSON
      filterFn: JSON_FIELDS.has(col.accessorKey)
        ? (row, columnId, filterValue) => {
            const value = row.getValue(columnId);
            if (!filterValue) return true;
            const searchTerm = filterValue.toLowerCase();
            
            // Convert value to string for searching
            let searchableText = "";
            try {
              if (typeof value === "object" && value !== null) {
                searchableText = JSON.stringify(value).toLowerCase();
              } else if (typeof value === "string") {
                try {
                  const parsed = JSON.parse(value);
                  searchableText = JSON.stringify(parsed).toLowerCase();
                } catch {
                  searchableText = value.toLowerCase();
                }
              } else {
                searchableText = String(value).toLowerCase();
              }
            } catch {
              searchableText = String(value || "").toLowerCase();
            }
            
            return searchableText.includes(searchTerm);
          }
        : undefined,
      Cell: ({ cell }) => {
        const value = cell.getValue();

        // Special handling for JSON fields
        if (JSON_FIELDS.has(col.accessorKey)) {
          if (!value && value !== 0 && value !== false) return "-";

          let jsonStr = "";
          try {
            if (typeof value === "object" && value !== null) {
              jsonStr = JSON.stringify(value, null, 2);
            } else if (typeof value === "string") {
              try {
                const parsed = JSON.parse(value);
                jsonStr = JSON.stringify(parsed, null, 2);
              } catch {
                jsonStr = value;
              }
            } else {
              jsonStr = String(value);
            }
          } catch (e) {
            jsonStr = String(value);
          }

          const displayText =
            jsonStr.length > 100 ? jsonStr.substring(0, 100) + "..." : jsonStr;

          return (
            <Tooltip
              title={
                <pre
                  style={{
                    fontSize: "0.75rem",
                    margin: 0,
                    maxWidth: "600px",
                    maxHeight: "400px",
                    overflow: "auto",
                    whiteSpace: "pre-wrap",
                    wordWrap: "break-word",
                  }}
                >
                  {jsonStr}
                </pre>
              }
              arrow
              placement="left"
              componentsProps={{
                tooltip: {
                  sx: {
                    bgcolor: "white",
                    color: "black",
                    border: "1px solid #e0e0e0",
                    fontSize: "0.75rem",
                    fontFamily: "monospace",
                    maxWidth: "650px",
                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                  },
                },
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontFamily: "monospace",
                  fontSize: "0.7rem",
                  cursor: "help",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  maxWidth: "250px",
                  display: "inline-block",
                }}
              >
                {displayText}
              </Typography>
            </Tooltip>
          );
        }

        // Highlight active field
        if (col.accessorKey === "active") {
          return (
            <Chip
              label={value ? "Yes" : "No"}
              size="small"
              color={value ? "success" : undefined}
              sx={!value ? { bgcolor: "grey.400", color: "white" } : {}}
            />
          );
        }

        return value;
      },
    }));

    // Add actions column
    cols.push({
      accessorKey: "actions",
      header: "Actions",
      size: 120,
      enableColumnFilter: false,
      enableSorting: false,
      enableHiding: false,
      enableColumnOrdering: false,
      enableResizing: false,
      muiTableHeadCellProps: {
        align: "center",
      },
      muiTableBodyCellProps: {
        align: "center",
      },
      Cell: ({ row }) => (
        <Box sx={{ display: "flex", gap: 0.5, justifyContent: "center" }}>
          <Tooltip title="Edit">
            <IconButton
              size="small"
              onClick={() => handleEditClick(row.original)}
              color="primary"
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              size="small"
              onClick={() => handleDeleteClick(row.original)}
              color="error"
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    });

    return cols;
  }, [data]);

  const formatDeletePreview = useCallback(
    (item) => {
      if (!item) return "";
      const fieldKeys = columns
        .map((col) => col.accessorKey)
        .filter((key) => key && key !== "actions");
      const filtered = fieldKeys.reduce((acc, key) => {
        if (Object.prototype.hasOwnProperty.call(item, key)) {
          acc[key] = item[key];
        }
        return acc;
      }, {});
      return JSON.stringify(sortKeysDeep(filtered), null, 2);
    },
    [columns],
  );

  // Memoize table data
  const tableData = useMemo(() => {
    return data.map((row) => {
      const processedRow = { ...row };
      Object.keys(processedRow).forEach((key) => {
        if (processedRow[key] === null) {
          processedRow[key] = "";
        }
      });
      return processedRow;
    });
  }, [data]);

  // Export handler
  const handleExportRows = (rows) => {
    const filteredRows = rows.map((row) => row.original);
    csvExporter.generateCsv(filteredRows);
  };

  // Generate form fields for the dialog
  const renderFormFields = () => {
    const fields = STATEMENTS_CONFIG.customColumns.map((col) => col.accessorKey);

    return fields.map((field) => {
      const value = currentItem[field] || "";
      const displayValue =
        typeof value === "object" ? JSON.stringify(value, null, 2) : value;

      // Special handling for 'active' field
      if (field === "active") {
        const isActive = value === 1 || value === "1" || value === true;
        return (
          <Box key={field} sx={{ mt: 2, mb: 1 }}>
            <Typography variant="body2" gutterBottom>
              {field.replace(/_/g, " ").toUpperCase()}
            </Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                variant={isActive ? "contained" : "outlined"}
                color="success"
                size="small"
                onClick={() => handleInputChange(field, true)}
              >
                Active
              </Button>
              <Button
                variant={!isActive ? "contained" : "outlined"}
                size="small"
                onClick={() => handleInputChange(field, false)}
                sx={{
                  color: !isActive ? "white" : "text.secondary",
                  bgcolor: !isActive ? "grey.600" : "transparent",
                  borderColor: "grey.600",
                  "&:hover": {
                    bgcolor: !isActive ? "grey.700" : "grey.50",
                    borderColor: "grey.700",
                  },
                }}
              >
                Inactive
              </Button>
            </Box>
          </Box>
        );
      }

      // Special handling for 'data_role' field
      if (field === "data_role" && availableDataRoles.length > 0) {
        return (
          <Autocomplete
            key={field}
            freeSolo
            options={availableDataRoles}
            value={value || ""}
            onChange={(event, newValue) => {
              handleInputChange(field, newValue);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                margin="normal"
                label="DATA ROLE"
                required
              />
            )}
          />
        );
      }

      // Special handling for 'query' field - show available queries from S3
      if (field === "query") {
        const queryOptions = availableQueries.map((q) => q.name);
        return (
          <Box key={field}>
            <Autocomplete
              freeSolo
              options={queryOptions}
              value={value || ""}
              onChange={async (event, newValue) => {
                handleInputChange(field, newValue || "");
                if (newValue) {
                  await fetchQueryDetails(newValue);
                } else {
                  setSelectedQueryDetails(null);
                }
              }}
              onInputChange={async (event, newInputValue) => {
                if (newInputValue && queryOptions.includes(newInputValue)) {
                  await fetchQueryDetails(newInputValue);
                } else if (!newInputValue) {
                  setSelectedQueryDetails(null);
                }
              }}
              loading={loadingQueries || loadingQueryDetails}
              renderInput={(params) => (
                <TextField
                  {...params}
                  margin="normal"
                  label="QUERY"
                  required
                  placeholder={
                    loadingQueries
                      ? "Loading queries..."
                      : "Select or type a query name"
                  }
                  helperText={
                    loadingQueries
                      ? "Loading queries from S3..."
                      : availableQueries.length > 0
                        ? `${availableQueries.length} queries available`
                        : "No queries found. Make sure queries are uploaded to S3."
                  }
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingQueries ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
            {selectedQueryDetails && (
              <Box sx={{ mt: 2, mb: 2 }}>
                {selectedQueryDetails.isDefault && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                      Default Fallback Logic
                    </Typography>
                    <Typography variant="body2">
                      This is a default query with no SQL associated. It serves as fallback logic 
                      for a role when no specific query logic is defined. You can still create filter 
                      logic, but you'll need to manually enter field names.
                    </Typography>
                  </Alert>
                )}
                {selectedQueryDetails.description && (
                  <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                    Description: {selectedQueryDetails.description}
                  </Typography>
                )}
                {selectedQueryDetails.sql && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>
                      SQL Query:
                    </Typography>
                    <Box
                      component="pre"
                      sx={{
                        backgroundColor: "#f5f5f5",
                        p: 1.5,
                        borderRadius: 1,
                        overflowX: "auto",
                        fontSize: "0.75rem",
                        fontFamily: "monospace",
                        maxHeight: "500px",
                        overflowY: "auto",
                        border: "1px solid #e0e0e0",
                      }}
                    >
                      {selectedQueryDetails.sql}
                    </Box>
                  </Box>
                )}
                {selectedQueryDetails.fields && selectedQueryDetails.fields.length > 0 && (
                  <Box>
                    <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>
                      Available Fields for Filter Logic:
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {selectedQueryDetails.fields.map((field, idx) => {
                        const label =
                          typeof field === "string"
                            ? field
                            : field?.name || "";
                        if (!label) {
                          return null;
                        }
                        const chipDescription =
                          typeof field === "object" ? field?.description : null;
                        return (
                          <Tooltip key={`${label}-${idx}`} title={chipDescription || ""}>
                            <Chip
                              label={label}
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                          </Tooltip>
                        );
                      })}
                    </Box>
                    <Typography
                      variant="caption"
                      sx={{ mt: 1, display: "block", color: "text.secondary" }}
                    >
                      Tip: Use these field names in your filter_logic JSON below
                    </Typography>
                  </Box>
                )}
                {selectedQueryDetails.isDefault && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    <Typography variant="body2">
                      No fields available for default query. You can manually enter field names in the filter logic builder.
                    </Typography>
                  </Alert>
                )}
              </Box>
            )}
          </Box>
        );
      }

      // Special handling for 'filter_logic' field
      if (field === "filter_logic") {
        return (
          <Box key={field} sx={{ mt: 2 }}>
            <FilterLogicBuilder
              value={value}
              onChange={(newValue) => {
                handleInputChange(field, newValue);
              }}
              availableFields={selectedQueryDetails?.fields || []}
              fieldMetadata={selectedQueryDetails?.fieldMetadata || {}}
              onValidationChange={(isValid) => {
                // Optional: You can use this to disable save button if invalid
              }}
            />
          </Box>
        );
      }

      if (field === "security_scopes") {
        return (
          <TextField
            key={field}
            fullWidth
            margin="normal"
            label="SECURITY SCOPES"
            value={displayValue}
            placeholder={`{\n  "companies": {\n    "field": "e.COMPANY"\n  }\n}`}
            helperText="Optional JSON. Use this to opt a statement into company security for a query-specific company field or alias."
            onChange={(e) => handleInputChange(field, e.target.value)}
            multiline
            rows={6}
            InputProps={{
              sx: {
                fontFamily: "monospace",
                fontSize: "0.85rem",
              },
            }}
          />
        );
      }

      // Skip id field in create mode
      if (
        field === "id" &&
        dialogMode === "create" &&
        STATEMENTS_CONFIG.autoGenerateId
      ) {
        return null;
      }

      // Show id in edit mode as read-only
      if (field === "id" && dialogMode === "edit") {
        return (
          <TextField
            key={field}
            fullWidth
            margin="normal"
            label="ID"
            value={displayValue}
            disabled
            helperText="Auto-generated ID cannot be modified"
          />
        );
      }

      const isRequired = ["data_role", "query", "filter_logic"].includes(field);

      return (
        <TextField
          key={field}
          fullWidth
          margin="normal"
          label={field.replace(/_/g, " ").toUpperCase()}
          value={displayValue}
          required={isRequired}
          placeholder={field === "query" ? "Enter query name" : ""}
          onChange={(e) => {
            let newValue = e.target.value;
            if (newValue.startsWith("{") || newValue.startsWith("[")) {
              try {
                newValue = JSON.parse(newValue);
              } catch {
                // Keep as string if not valid JSON
              }
            }
            handleInputChange(field, newValue);
          }}
          multiline={typeof value === "object" || (typeof value === "string" && value.length > 50)}
          rows={typeof value === "object" ? 4 : 1}
        />
      );
    });
  };

  return (
    <>
      <Box sx={{ flex: 1, p: 2 }}>
        <MaterialReactTable
          columns={columns}
          data={tableData}
          initialState={{
            showColumnFilters: true,
            density: "compact",
            pagination: {
              pageSize: pagination.pageSize,
              pageIndex: pagination.pageIndex,
            },
            columnPinning: { right: ["active", "actions"] },
          }}
          muiTableContainerProps={{ sx: { maxHeight: "600px" } }}
          enableStickyHeader
          enableFullScreenToggle={false}
          enableColumnOrdering={false}
          enableColumnDragging={false}
          enableColumnPinning
          enableHiding={false}
          enableColumnFilters={true}
          enableSorting
          enableMultiSort={false}
          enablePagination
          enableBottomToolbar
          paginationDisplayMode="pages"
          manualPagination
          pageCount={-1}
          onPaginationChange={(updater) => {
            if (typeof updater === "function") {
              const newState = updater({
                pageIndex: pagination.pageIndex,
                pageSize: pagination.pageSize,
              });
              handlePaginationChange(newState.pageIndex, newState.pageSize);
            } else {
              handlePaginationChange(updater.pageIndex, updater.pageSize);
            }
          }}
          muiTablePaginationProps={{
            rowsPerPageOptions: [25, 50, 100, 200, 500],
            showFirstButton: true,
            showLastButton: true,
            labelRowsPerPage: "Rows per page:",
            labelDisplayedRows: ({ from, to, count }) => {
              const startRecord =
                pagination.pageIndex * pagination.pageSize + 1;
              const endRecord = Math.min(
                startRecord + pagination.pageSize - 1,
                pagination.totalCount,
              );
              return `${startRecord}-${endRecord} of ${pagination.hasNextPage ? `more than ${endRecord}` : pagination.totalCount}`;
            },
          }}
          muiToolbarAlertBannerProps={
            error
              ? {
                  color: "error",
                  children: error,
                }
              : undefined
          }
          state={{
            isLoading: loading,
            showProgressBars: loading,
            pagination: {
              pageIndex: pagination.pageIndex,
              pageSize: pagination.pageSize,
            },
          }}
          renderTopToolbarCustomActions={({ table }) => (
            <Box
              sx={{
                display: "flex",
                gap: "1rem",
                p: "0.5rem",
                flexWrap: "wrap",
              }}
            >
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={handleAddClick}
                variant="contained"
                color="primary"
              >
                Add Statement
              </Button>
              <Button
                size="small"
                startIcon={<RefreshIcon />}
                onClick={handleRefresh}
                variant="outlined"
              >
                Refresh
              </Button>
              {pagination.hasNextPage && (
                <Button
                  size="small"
                  onClick={() =>
                    handlePaginationChange(
                      pagination.pageIndex + 1,
                      pagination.pageSize,
                    )
                  }
                  variant="contained"
                  color="secondary"
                  disabled={loading}
                >
                  {`Load Next ${pagination.pageSize} Rows`}
                </Button>
              )}
              <IconButton
                onClick={() =>
                  handleExportRows(table.getPrePaginationRowModel().rows)
                }
                title="Export to CSV"
              >
                <CloudDownloadIcon />
              </IconButton>
            </Box>
          )}
        />
      </Box>

      {/* Create/Edit Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleDialogClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {dialogMode === "create" ? "Create New Statement" : "Edit Statement"}
          {editingItem && (
            <Typography
              variant="caption"
              display="block"
              color="text.secondary"
            >
              {STATEMENTS_CONFIG.primaryKey}:{" "}
              {editingItem[STATEMENTS_CONFIG.primaryKey]}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>{renderFormFields()}</Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" color="primary">
            {dialogMode === "create" ? "Create" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={handleDeleteCancel}
        maxWidth="xs"
        fullWidth
        disableEscapeKeyDown
      >
        <DialogTitle>Delete Statement</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            <Typography variant="body1" gutterBottom>
              Are you sure you want to delete this statement?
            </Typography>
            {itemPendingDelete && (
              <Box
                component="pre"
                sx={{
                  backgroundColor: "#f5f5f5",
                  p: 2,
                  borderRadius: 1,
                  overflowX: "auto",
                  fontSize: "0.85rem",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
              >
                {formatDeletePreview(itemPendingDelete)}
              </Box>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={deleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {deleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      {createPortal(
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={4000}
          onClose={() => setSnackbarOpen(false)}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          sx={{ zIndex: 1500 }}
        >
          <Alert
            onClose={() => setSnackbarOpen(false)}
            severity={snackbarSeverity}
            variant="filled"
            sx={{ fontSize: "0.875rem" }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>,
        document.body,
      )}
    </>
  );
}

